import { Content, ContentDataSignedUrl, ContentDataStatus } from "./ResearchDataModel"
import { useResearchDataApi } from './ResearchDataApi'
import { AsyncData, LoadMode } from "@webapp/util/AsyncData"
import { AsyncDataStore } from "@webapp/util/AsyncDataStore"
import { defineStore } from "pinia"
import { DateTime } from "luxon"
import { DateTimeUtil } from "@webapp/util/LuxonUtil"
import { assignExisting, isDefined } from "@webapp/util/TypeScriptUtil"
import { HistoricalDate } from './HistoricalDate'
import { uniqBy } from 'lodash'


export const useContentStore = defineStore("contentStore", () => {
    const asyncContents = new Map<string, AsyncData<Content>>()

    const rd = useResearchDataApi()

    function getLoadedContents(ids: (string | undefined)[]) {
        const store = useContentStore() // use to get/return reactive store objects
        return ids.filter(isDefined).map(id => store.asyncContents.get(id)?.data).filter(isDefined)
    }

    function trackAsyncContents(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const loadAsync = async (ids: string[], requestTime: DateTime) => {
            const plainItems = await rd.getPlainByIdsAsync("content", ids, 'dataSignedUrls')
            console.log(`Loaded ${plainItems.length} data groups by id: ${plainItems.map(p => p.id).join(", ")}`)
            return processExpandedContents(plainItems, requestTime)
        }
        const store = useContentStore() // use to get/return reactive store objects
        return AsyncDataStore.getAsyncItemsByIds(store.asyncContents, ids, "Content", loadMode, loadAsync)
    }

    function getContents(ids: (string | undefined)[], loadMode = LoadMode.TrackOnly) {
        const asyncItems = trackAsyncContents(ids, loadMode)
        return asyncItems.map(a => a.data).filter(isDefined)
    }

    async function getContentsAsync(ids: (string | undefined)[]) {
        return (await Promise.all(trackAsyncContents(ids, LoadMode.EnsureLoaded).map(a => a.whenLoadCompleted))).filter(isDefined)
    }

    async function addAsync(groupId: string, content: Content) {
        const store = useContentStore() // use to get/return reactive store objects
        const addCoreAsync = async () => {
            const protocolItem = {
                ...content,
                date: content.date?.toString(),
            }
            const p = await rd.postPlainAsync(`groups/${groupId}/content`, protocolItem)
            return plainToContent(p)
        }
        return await AsyncDataStore.addToStoreMapAsync(store.asyncContents, "Content", c => c.id, addCoreAsync)
    }

    function addContentsToStore(plainContents: any[], requestTime: DateTime) {
        const store = useContentStore() // use to get/return reactive store objects
        const contentMap = processExpandedContents(uniqBy(plainContents, 'id'), requestTime)
        AsyncDataStore.addItemMapToStore(store.asyncContents, "Content", contentMap, requestTime)
    }

    function processExpandedContents(plainItems: any[], requestTime: DateTime) {
        return AsyncDataStore.createItemMap(plainItems, "Content", plainToContent, c => c.id!)
    }

    return {
        asyncContents,

        getLoadedContents,
        trackAsyncContents,
        getContents,
        getContentsAsync,
        addAsync,
        addContentsToStore,
    }
})

function plainToContent(p: any) {
    const content = assignExisting(new Content(), p)
    content.date = HistoricalDate.parse(p.date)
    content.dataStatus = p.dataStatus ?? ContentDataStatus.NoData
    content.dataModifiedDate = DateTimeUtil.fromAPI(p.dataModifiedDate)
    content.dataSignedUrls = p.dataSignedUrls?.map(plainToSignedUrl)
    content.profile ??= false
    content.modifiedDate = DateTimeUtil.fromAPI(p.modifiedDate)
    content.createdDate = DateTimeUtil.fromAPI(p.createdDate)

    // Fake reactions for prototype — remove when backend provides real data
    //if (!content.reactions) {
        //content.reactions = { likeCount: 2 + ((content.id?.charCodeAt(5) ?? 0) % 7) }
    //}

    return content
}

export function plainToSignedUrl(p: any) {
    if (p) {
        p.expirationDate = DateTimeUtil.fromAPI(p.expirationDate)
        return p as ContentDataSignedUrl
    }
}
